/**
 * API Implementation of IUserPreferenceService
 *
 * Endpoints:
 *  GET    /app/learner-setting              → full settings object
 *  PUT    /app/learner-setting              → bulk update (all fields required)
 *  PATCH  /app/learner-setting/furigana     → { isFuriganaEnabled }
 *  PATCH  /app/learner-setting/translation  → { isTranslationEnabled }
 *  PATCH  /app/learner-setting/language-code      → { languageCode }
 *  PATCH  /app/learner-setting/push-notification  → { isPushNotificationEnabled }
 *  PATCH  /app/learner-setting/recommendation     → { isRecommendationEnabled }
 */

import { clientApi } from "@/utils/api";
import { TApiRes } from "@/types/api";
import { SupportedLanguage, DEFAULT_LANGUAGE } from "@/constants/languages";
import {
  UserLanguagePreference,
  UserStudySettings,
  UpdateStudySettingsPayload,
  LearnerSettingDTO,
} from "@/infrastructure/adapters/user-preference/user-preference.types";
import { IUserPreferenceService } from "@/infrastructure/services/user-preference/user-preference.service";

/** Deduplicate concurrent GET calls (getLanguage + getStudySettings hit same endpoint) */
let pendingGet: Promise<LearnerSettingDTO> | null = null;

const fetchLearnerSetting = async (): Promise<LearnerSettingDTO> => {
  if (pendingGet) return pendingGet;

  pendingGet = clientApi
    .get<TApiRes<LearnerSettingDTO>>("/learner-setting")
    .then((res) => res.data.data)
    .finally(() => {
      pendingGet = null;
    });

  return pendingGet;
};

const mapToLanguagePreference = (dto: LearnerSettingDTO): UserLanguagePreference => ({
  language: (dto.languageCode as SupportedLanguage) || DEFAULT_LANGUAGE,
  updatedAt: new Date(),
});

const mapToStudySettings = (dto: LearnerSettingDTO): UserStudySettings => ({
  isFuriganaEnabled: dto.isFuriganaEnabled,
  isTranslationEnabled: dto.isTranslationEnabled,
  // API returns 0 when not set — treat as null so UI shows placeholder
  weeklyStudyTimeTarget: dto.weeklyStudyTimeTarget || null,
  isPushNotificationEnabled: dto.isPushNotification ?? false,
  isRecommendationEnabled: dto.isRecommendation ?? true,
  isBoostVisible: dto.isBoostVisible ?? false,
});

export const apiUserPreferenceService: IUserPreferenceService = {
  getLanguage: async () => {
    const dto = await fetchLearnerSetting();
    return mapToLanguagePreference(dto);
  },

  setLanguage: async (language: SupportedLanguage) => {
    await clientApi.patch("/learner-setting/language-code", { value: language });
    return { language, updatedAt: new Date() };
  },

  getStudySettings: async () => {
    const dto = await fetchLearnerSetting();
    return mapToStudySettings(dto);
  },

  updateStudySettings: async (settings: UpdateStudySettingsPayload) => {
    if (settings.weeklyStudyTimeTarget !== undefined) {
      // Bulk update — PUT all fields at once (onboarding saves everything together)
      const current = await fetchLearnerSetting();
      await clientApi.put<TApiRes<LearnerSettingDTO>>("/learner-setting", {
        isFuriganaEnabled: settings.isFuriganaEnabled ?? current.isFuriganaEnabled,
        isTranslationEnabled: settings.isTranslationEnabled ?? current.isTranslationEnabled,
        weeklyStudyTimeTarget: settings.weeklyStudyTimeTarget,
        languageCode: current.languageCode,
        isPushNotification: settings.isPushNotificationEnabled ?? current.isPushNotification,
        isRecommendation: settings.isRecommendationEnabled ?? current.isRecommendation,
      });
    } else {
      // Individual toggle — use PATCH for the changed field only
      if (settings.isFuriganaEnabled !== undefined) {
        await clientApi.patch("/learner-setting/furigana", {
          value: settings.isFuriganaEnabled,
        });
      }

      if (settings.isTranslationEnabled !== undefined) {
        await clientApi.patch("/learner-setting/translation", {
          value: settings.isTranslationEnabled,
        });
      }

      if (settings.isPushNotificationEnabled !== undefined) {
        await clientApi.patch("/learner-setting/push-notification", {
          value: settings.isPushNotificationEnabled,
        });
      }

      if (settings.isRecommendationEnabled !== undefined) {
        await clientApi.patch("/learner-setting/recommendation", {
          value: settings.isRecommendationEnabled,
        });
      }

      if (settings.isBoostVisible !== undefined) {
        await clientApi.patch("/learner-setting/boost", {
          value: settings.isBoostVisible,
        });
      }
    }

    return settings as UserStudySettings;
  },
};
